import {
  ArrowLeftStroke,
  FileMultipleStroke,
  FloppyDisk1Stroke,
  Pencil1Stroke,
  PlusStroke,
  PrinterStroke,
  RefreshCircle1ClockwiseStroke,
  SignPostLeftStroke,
  Trash3Stroke
} from '@lineiconshq/free-icons';
import { useMemo, useState } from 'react';
import { exportCreditAdviceSchema, initialFormValues, type ExportCreditAdviceFormValues } from '../../../schemas';
import { calculatePaymentTotals } from '../../../utils/calc-utils';
import { useBankFeeRows, useDetailItemRows } from '../tabs/hooks';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { ActionButtonV2 } from '@/components/custom/erp';
import { transformFormData } from '../../../utils';
import { useFormFieldState } from '../../../hooks';
import { useAuth } from '@/contexts/auth-context';
import type { AddFormProps } from '../types';

export const useFormDialog = ({
  formMode,
  initialData,
  onSubmit,
  onClose,
  handleAddClick,
  handleEditClick,
  handleDeleteClick,
  handleCopyClick
}: AddFormProps) => {
  const [activeTab] = useState('info');
  const detailRows = useDetailItemRows(initialData?.chi_tiet_data || initialData?.chi_tiet_giay_bao_co_data || []);
  const bankFeeRows = useBankFeeRows(initialData?.phieu_ngan_hang_giay_bao_co_data || []);
  const repeatDetailRows = useRepeatRowsHandler({
    setRows: detailRows.setRows
  });
  const repeatBankFeeRows = useRepeatRowsHandler({
    setRows: bankFeeRows.setRows
  });
  const { state, actions } = useFormFieldState(initialData);
  const { entityUnit } = useAuth();

  const totals = useMemo(
    () => calculatePaymentTotals(detailRows.rows, bankFeeRows.rows),
    [detailRows.rows, bankFeeRows.rows]
  );
  const initialValues = useMemo(
    () =>
      initialData
        ? {
            ...initialFormValues,
            ...initialData,
            ma_ngv: initialData.ma_ngv ?? initialFormValues.ma_ngv,
            dia_chi: initialData.dia_chi ?? '',
            ong_ba: initialData.ong_ba ?? '',
            dien_giai: initialData.dien_giai ?? '',
            ma_nt: initialData.ma_nt ?? '',
            ngay_ct: initialData.ngay_ct ?? initialFormValues.ngay_ct,
            ngay_lct: initialData.ngay_lct ?? initialFormValues.ngay_lct,
            ngay_ct0: initialData.ngay_ct0 ?? '',
            ty_gia: Number(initialData.ty_gia ?? initialFormValues.ty_gia)
          }
        : initialFormValues,
    [initialData]
  );

  const submit = async (data: ExportCreditAdviceFormValues) => {
    const formData = transformFormData(
      data,
      { state, actions }.state,
      formMode,
      detailRows.rows,
      bankFeeRows.rows,
      entityUnit
    );
    await onSubmit(formData);
  };

  const title = useMemo(() => {
    if (formMode !== 'view') {
      return formMode === 'add' ? 'Mới' : 'Sửa';
    }

    switch (activeTab) {
      case 'history':
        return 'Lịch sử';
      default:
        return 'Giấy báo có';
    }
  }, [activeTab, formMode]);

  const actionButtons = useMemo(() => {
    if (formMode !== 'view') {
      return (
        <>
          <ActionButtonV2 icon={FloppyDisk1Stroke} title='Lưu' variant='secondary' type='submit' />
          <ActionButtonV2 icon={ArrowLeftStroke} title='Đóng' variant='destructive' onClick={onClose} />
        </>
      );
    }

    if (activeTab === 'history') {
      return (
        <>
          <ActionButtonV2 icon={RefreshCircle1ClockwiseStroke} title='Refresh' variant='secondary' onClick={() => {}} />
          <ActionButtonV2 icon={SignPostLeftStroke} title='Cố định cột' variant='secondary' onClick={() => {}} />
          <ActionButtonV2 icon={ArrowLeftStroke} title='Đóng' variant='destructive' onClick={onClose} />
        </>
      );
    }

    return (
      <>
        <ActionButtonV2 icon={PrinterStroke} title='In' variant='secondary' onClick={() => {}} />
        <ActionButtonV2 icon={PlusStroke} title='Thêm' variant='primary' onClick={handleAddClick} />
        <ActionButtonV2 icon={Pencil1Stroke} title='Sửa' variant='secondary' onClick={handleEditClick} />
        <ActionButtonV2 icon={Trash3Stroke} title='Xóa' variant='destructive' onClick={handleDeleteClick} />
        <ActionButtonV2 icon={FileMultipleStroke} title='Sao chép' variant='secondary' onClick={handleCopyClick} />
        <ActionButtonV2 icon={ArrowLeftStroke} title='Đóng' variant='destructive' onClick={onClose} />
      </>
    );
  }, [activeTab, formMode, handleAddClick, handleCopyClick, handleDeleteClick, handleEditClick, onClose]);

  const customer = {
    code: detailRows.rows?.[0]?.ma_kh_data?.customer_code || initialData?.ma_kh_data?.customer_code || '',
    name: detailRows.rows?.[0]?.ma_kh_data?.customer_name || initialData?.ma_kh_data?.customer_name || ''
  };

  return {
    formMode,
    initialData,
    initialValues,
    schema: exportCreditAdviceSchema,
    title,
    activeTab,
    onClose,
    submit,
    actionButtons,
    totals,
    customer,
    formState: { state, actions },
    detail: {
      rows: detailRows.rows,
      tableProps: {
        detailRows: detailRows.rows,
        detailSelectedRowUuid: detailRows.selectedRowUuid,
        detailSelectedCell: detailRows.selectedCell,
        detailHandleRowClick: detailRows.handleRowClick,
        detailHandleCellClick: detailRows.handleCellClick,
        detailHandleAddRow: detailRows.handleAddRow,
        detailHandleDeleteRow: detailRows.handleDeleteRow,
        detailHandleCopyRow: detailRows.handleCopyRow,
        detailHandlePasteRow: detailRows.handlePasteRow,
        detailHandleMoveRow: detailRows.handleMoveRow,
        detailHandleCellValueChange: detailRows.handleCellValueChange,
        detailHandleUpdateNonWritableFields: detailRows.setNonWritableFields,
        detailHandleUpdatePasteConfigs: detailRows.setPasteConfigs,
        onRepeatForRows: repeatDetailRows,
        isPasting: detailRows.isPasting
      }
    },
    bankFee: {
      rows: bankFeeRows.rows,
      tableProps: {
        rows: bankFeeRows.rows,
        selectedRowUuid: bankFeeRows.selectedRowUuid,
        selectedCell: bankFeeRows.selectedCell,
        onRowClick: bankFeeRows.handleRowClick,
        onCellClick: bankFeeRows.handleCellClick,
        onAddRow: bankFeeRows.handleAddRow,
        onDeleteRow: bankFeeRows.handleDeleteRow,
        onCopyRow: bankFeeRows.handleCopyRow,
        onPasteRow: bankFeeRows.handlePasteRow,
        onMoveRow: bankFeeRows.handleMoveRow,
        onCellValueChange: bankFeeRows.handleCellValueChange,
        handleUpdateNonWritableFields: bankFeeRows.setNonWritableFields,
        handleUpdatePasteConfigs: bankFeeRows.setPasteConfigs,
        onRepeatForRows: repeatBankFeeRows,
        isPasting: bankFeeRows.isPasting
      }
    }
  };
};
